// Dashboards — AI-managed dashboards (DEV: dashboards/widgets). Rows are
// created and updated by orchestration agents (dashboard_* tools) or external
// MCP callers; the console is read-only chrome — view, star, delete — plus
// the ONE human-managed knob: the org-wide aesthetics guidance agents read
// via dashboard_get_guidance before authoring widget HTML.
import { useClient } from '@cortex/react-query';
import type { DashboardGuidance, DashboardListItem } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { Table, type IColumn } from '../../components/common/Table';
import { routes } from '../../lib/constants';
import { formatRelative } from '../../lib/format';

export const Route = createFileRoute('/dashboards/')({ component: DashboardsPage });

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

function listKey() {
  return ['dashboards'] as const;
}

function createdBy(row: DashboardListItem): string {
  if (!row.createdByType) return '—';
  return row.createdByType === 'agent'
    ? `agent ${row.createdById ?? ''}`
    : (row.createdById ?? 'user');
}

/**
 * The org's aesthetics guidance — a human-written style brief agents read
 * via dashboard_get_guidance before authoring or restyling widgets. Lives
 * behind a button + modal (not inline) since the free-text can run long
 * enough to push the dashboards table awkwardly far down the page.
 */
function AestheticsButton() {
  const client = useClient();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const guidance = useQuery({
    queryKey: ['dashboards', 'guidance'],
    queryFn: () => client.http.get('v1/dashboards/guidance').json<DashboardGuidance>(),
  });

  const save = useMutation({
    mutationFn: (text: string) => client.http.put('v1/dashboards/guidance', { json: { text } }),
    onSuccess: () => {
      setOpen(false);
      return qc.invalidateQueries({ queryKey: ['dashboards', 'guidance'] });
    },
  });

  return (
    <>
      <button
        type="button"
        className="btn btn--sm btn--ghost"
        onClick={() => {
          setDraft(guidance.data?.text ?? '');
          setOpen(true);
        }}
      >
        Configure Aesthetics
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Aesthetics guidance"
        footer={
          <>
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => setOpen(false)}
              disabled={save.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--sm btn--primary"
              onClick={() => save.mutate(draft)}
              disabled={save.isPending}
            >
              Save
            </button>
          </>
        }
      >
        <p className="t-xs t-muted" style={{ marginTop: 0 }}>
          Agents read this (dashboard_get_guidance) and follow it when building widgets.
        </p>
        <textarea
          className="input"
          style={{ width: '100%', minHeight: 220, resize: 'vertical' }}
          value={draft}
          maxLength={8000}
          placeholder="Palette, typography, tone… e.g. 'Dark-first, minimal chrome, accent #3661b0, tabular numerals for metrics.'"
          onChange={(e) => setDraft(e.target.value)}
        />
        {save.error ? (
          <p className="t-xs" style={{ color: 'var(--color-danger)' }}>
            Save failed: {(save.error as Error).message}
          </p>
        ) : null}
      </Modal>
    </>
  );
}

function DashboardsPage() {
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();

  const list = useQuery({
    queryKey: listKey(),
    queryFn: () => client.http.get('v1/dashboards').json<IListEnvelope<DashboardListItem>>(),
  });

  const remove = useMutation({
    mutationFn: (id: string) => client.http.delete(`v1/dashboards/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey() }),
  });

  // Per-user star (drives the nav rail's Favorites folder). Optimistic: flip
  // the row locally, reconcile with the server on settle.
  const toggleFavorite = useMutation({
    mutationFn: ({ id, favorite }: { id: string; favorite: boolean }) =>
      favorite
        ? client.http.put(`v1/dashboards/${id}/favorite`, { json: {} })
        : client.http.delete(`v1/dashboards/${id}/favorite`),
    onMutate: async ({ id, favorite }) => {
      await qc.cancelQueries({ queryKey: listKey() });
      qc.setQueryData<IListEnvelope<DashboardListItem>>(listKey(), (prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((row) =>
                row.id === id ? { ...row, isFavorite: favorite } : row,
              ),
            }
          : prev,
      );
    },
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: listKey() }),
        qc.invalidateQueries({ queryKey: ['nav', 'dashboards'] }),
      ]),
  });

  const columns: IColumn<DashboardListItem>[] = [
    {
      key: 'favorite',
      header: '',
      cell: (row) => (
        <button
          type="button"
          className="btn btn--sm btn--ghost"
          title={row.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          aria-label={row.isFavorite ? 'Unstar dashboard' : 'Star dashboard'}
          aria-pressed={Boolean(row.isFavorite)}
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite.mutate({ id: row.id, favorite: !row.isFavorite });
          }}
          style={{ color: row.isFavorite ? 'var(--color-warning, #d4a72c)' : 'var(--fg-muted)' }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill={row.isFavorite ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1 6.2-5.5-2.9-5.5 2.9 1-6.2L3 9.6l6.2-.9z" />
          </svg>
        </button>
      ),
    },
    { key: 'name', header: 'Name', cell: (row) => <strong>{row.name}</strong> },
    {
      key: 'kind',
      header: 'Kind',
      cell: (row) => <span className="badge">{row.kind === 'single' ? 'Full page' : 'Grid'}</span>,
    },
    { key: 'widgets', header: 'Widgets', numeric: true, cell: (row) => row.widgetCount },
    { key: 'createdBy', header: 'Created by', mono: true, cell: createdBy },
    { key: 'updated', header: 'Updated', cell: (row) => formatRelative(row.updatedAt) },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <button
          type="button"
          className="btn btn--sm btn--ghost"
          disabled={remove.isPending}
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm(`Delete dashboard "${row.name}"? This cannot be undone.`)) {
              remove.mutate(row.id);
            }
          }}
        >
          Delete
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Dashboards" actions={<AestheticsButton />} />
      <Table
        rows={list.data?.items}
        columns={columns}
        rowKey={(row) => row.id}
        isLoading={list.isLoading}
        error={list.error as Error | null}
        emptyTitle="No dashboards yet"
        emptyDescription="Dashboards are created by AI agents — enable 'Can create dashboards/widgets' on an orchestration agent, or use the dashboard_* MCP tools."
        onRowClick={(row) => nav({ to: routes.dashboardDetail(row.id) })}
      />
    </>
  );
}
